import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { useAdminOrganizationListingsQuery } from '@/client/api/admin/organizations';
import { Button } from '@/components/ui/button';

import EntityTable from '@/client/pages/Admin/UsersAndPermissions/UserDetailPage/tabs/components/EntityTable';

const PAGE_SIZE = 12;

const ListingsTab = ({ oid }: { oid: number }) => {
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, isFetching } = useAdminOrganizationListingsQuery(oid, {
    page,
    page_size: PAGE_SIZE,
  });

  const entities = data?.entities ?? [];
  const totalPages = Math.max(1, data?.total_pages ?? 1);
  const total = data?.total_entities ?? entities.length;
  const clampedPage = Math.min(page, totalPages);

  if (isError) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-900">
        <AlertTriangle className="mx-auto mb-2 h-6 w-6 text-amber-500" />
        <p className="font-medium">Couldn&apos;t load this organization&apos;s listings.</p>
        <p className="mx-auto mt-1 max-w-md text-amber-800">
          This tab reads{' '}
          <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">
            GET /api/admin/organizations/{oid}/listings
          </code>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          {isLoading ? 'Loading listings…' : `${total} listing${total === 1 ? '' : 's'}`}
        </p>
        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </div>

      <EntityTable
        entities={entities}
        isLoading={isLoading}
        emptyMessage="This organization has no listings."
      />

      {!isLoading && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            Page {clampedPage} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={clampedPage <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={clampedPage >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingsTab;
